<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
  onClick: { type: Function, default: null },
  isCollapsed: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle']);

const handleClick = event => {
  if (props.onClick) {
    event.preventDefault();
    event.stopPropagation();
    props.onClick();
  } else {
    emit('toggle');
  }
};

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
</script>

<template>
  <component
    :is="onClick ? 'div' : to ? 'router-link' : 'div'"
    class="flex items-center gap-2 rounded-lg min-w-0"
    :class="[
      isCollapsed ? 'px-2 py-2 h-10 w-10 justify-center' : 'px-2 py-1.5 h-8',
      {
        'text-n-blue-text bg-n-blue-3 font-semibold':
          isActive || hasActiveChild,
        'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
      },
    ]"
    role="button"
    draggable="false"
    :to="onClick ? undefined : to"
    :title="label"
    @click.stop="handleClick"
  >
    <div
      v-if="icon"
      class="relative flex items-center"
      :class="{ 'gap-2': !isCollapsed }"
    >
      <Icon
        v-if="icon"
        :icon="icon"
        :class="isCollapsed ? 'size-5' : 'size-4'"
      />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
      <span
        v-if="dynamicCount && !expandable && isCollapsed"
        class="absolute -top-1 ltr:-right-1 rtl:-left-1 rounded-full text-xs leading-none font-medium text-center px-1 py-0.5 min-w-[16px] bg-n-brand text-white"
      >
        {{ count }}
      </span>
    </div>
    <div
      v-if="!isCollapsed"
      class="flex items-center gap-1.5 flex-grow min-w-0"
    >
      <span class="text-sm font-medium leading-5 truncate">
        {{ label }}
      </span>
      <span
        v-if="dynamicCount && !expandable"
        class="rounded-md capitalize text-xs leading-5 font-medium text-center outline outline-1 px-1 flex-shrink-0"
        :class="{
          'text-n-blue-text outline-n-slate-6': isActive,
          'text-n-slate-11 outline-n-strong': !isActive,
        }"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="expandable && !isCollapsed"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-3"
      @click.stop="handleClick"
    />
  </component>
</template>
